import * as React from "react";
import {useTheme} from "@mui/material/styles";
import Box from "@mui/material/Box";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import {DataGrid} from "@mui/x-data-grid";
import IconButton from "@mui/material/IconButton";
import LaunchIcon from "@mui/icons-material/Launch";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import MDButton from "components/MDButton";
import properties from "constants/ApplicationConstants";
import {GET, PUT} from "APIclient";
import {formatDate} from "constants/ApplicationConstants";
import {
  FirstPage,
  LastPage,
  NavigateBefore,
  NavigateNext,
} from "@mui/icons-material";
import { Tooltip, Typography } from "@mui/material";

const columns = [
  {field: "id", headerName: "#", width: 70},
  {field: "youtubeLink", headerName: "Youtube Link", width: 300},
  {field: "addedOn", headerName: "Added On", width: 150},
  {field: "status", headerName: "Status", width: 120},
  {
    field: "action",
    headerName: "Action",
    width: 150,
    sortable: false,
    renderCell: params => {
      const isClosed = params.row.status === "Closed";
      return (
        <Box sx={{display: "flex", gap: 0.5}}>
          <IconButton
            size="small"
            disabled={isClosed}
            sx={{
              backgroundColor: isClosed ? "#e0e0e0" : "#5bc0de",
              color: "white",
              "&:hover": {
                backgroundColor: isClosed ? "#e0e0e0" : "#46b8da"
              },
              width: "30px",
              height: "30px",
              borderRadius: "4px"
            }}
          >
            <LaunchIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            disabled={isClosed}
            sx={{
              backgroundColor: isClosed ? "#e0e0e0" : "#f0ad4e",
              color: "white",
              "&:hover": {
                backgroundColor: isClosed ? "#e0e0e0" : "#ec971f"
              },
              width: "30px",
              height: "30px",
              borderRadius: "4px"
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            disabled={isClosed}
            sx={{
              backgroundColor: isClosed ? "#5cb85c" : "#d9534f",
              color: "white",
              "&:hover": {
                backgroundColor: isClosed ? "#449d44" : "#c9302c"
              },
              width: "30px",
              height: "30px",
              borderRadius: "4px"
            }}
            onClick={() => params.row.handleClose(params.row)}
          >
            {isClosed ? <CheckIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
          </IconButton>
        </Box>
      );
    }
  }
];

// Custom Pagination Component
function CustomPagination({
  page,
  pageSize,
  totalRows,
  totalPages,
  onPageChange,
  onGoToLastPage,
  loading,
}) {
  const handleFirstPage = () => onPageChange(0);
  const handlePreviousPage = () => onPageChange(Math.max(0, page - 1));
  const handleNextPage = () => onPageChange(Math.min(totalPages - 1, page + 1));
  const handleLastPage = () => onGoToLastPage();

  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, totalRows);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1,
        borderTop: "1px solid #e0e0e0",
      }}
    >
      <Typography variant="caption" sx={{ color: "#666" }}>
        {totalRows > 0 ? `${startRow}-${endRow} of ${totalRows}` : "No rows"}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="caption" sx={{ mr: 2, color: "#666" }}>
          Page {page + 1} of {totalPages || 1}
        </Typography>

        <Tooltip title="First Page">
          <IconButton
            onClick={handleFirstPage}
            disabled={page === 0 || loading}
            size="small"
          >
            <FirstPage />
          </IconButton>
        </Tooltip>

        <Tooltip title="Previous Page">
          <IconButton
            onClick={handlePreviousPage}
            disabled={page === 0 || loading}
            size="small"
          >
            <NavigateBefore />
          </IconButton>
        </Tooltip>

        <Tooltip title="Next Page">
          <IconButton
            onClick={handleNextPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
          >
            <NavigateNext />
          </IconButton>
        </Tooltip>

        <Tooltip title="Last Page">
          <IconButton
            onClick={handleLastPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
            sx={{
              backgroundColor: properties?.colors?.bgcolorbluethem || "#1976d2",
              color: "white",
              "&:hover": {
                backgroundColor:
                  properties?.colors?.bgcolorbluethem || "#1565c0",
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0",
                color: "#9e9e9e",
              },
            }}
          >
            <LastPage />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default function YoutubeLinksList() {
  const theme = useTheme();
  const [jobStatus, setJobStatus] = React.useState("");
  const [youtubeLinks, setYoutubeLinks] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  // Pagination states
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);
  const [rowCount, setRowCount] = React.useState(0);
  const [paginationModel, setPaginationModel] = React.useState({page: 0, pageSize: 10});
  const [totalRows, setTotalRows] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);

  const fetchYoutubeLinks = async (status = "", page = 0, size = 10) => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (status !== "") params.append("status", status);
      params.append("page", page);
      params.append("size", size);

      const response = await GET(`admin/youtube-links?${params.toString()}`);

      if (response && response.data) {
        const paginatedData = response.data.data?.paginatedData || response.data.data || [];
        const totalItems = response.data.data?.totalItems || paginatedData.length;
        const totalPages = response?.data?.data?.totalPages || 0;

        const transformedData = paginatedData.map((item, index) => ({
          id: page * size + index + 1,
          youtubeLink: item.youtubeLink || item.youtube_link || "",
          addedOn: formatDate(item.addedOn || item.added_on || item.createdAt || ""),
          status: item.status === 1 || item.status === "active" ? "Open" : "Closed",
          originalId: item.id,
          handleClose: handleClose
        }));

        setYoutubeLinks(transformedData);
        setRowCount(totalItems);
        setTotalRows(totalItems);
        setTotalPages(totalPages);
      } else {
        setYoutubeLinks([]);
        setRowCount(0);
      }
    } catch (err) {
      console.error("Error fetching YouTube links:", err);
      setError("Failed to load YouTube links. Please try again.");
      setYoutubeLinks([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = async row => {
    try {
      setLoading(true);
      setError(null);
      console.log("Closing YouTube link:", row);

      const youtubeLinkDTO = {
        youtubeLink: row.youtubeLink,
        status: 0
      };

      const response = await PUT(`admin/update-youtube-link/${row.originalId}`, youtubeLinkDTO);

      if (response.status === 200) {
        setYoutubeLinks(prevLinks =>
          prevLinks.map(link =>
            link.originalId === row.originalId ? {...link, status: "Closed"} : link
          )
        );
      } else {
        setError("Failed to update YouTube link status.");
      }
    } catch (err) {
      console.error("Error updating YouTube link:", err);
      setError("Failed to update YouTube link status. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchYoutubeLinks(jobStatus, page, pageSize);
  }, []);

  const handleJobStatusChange = event => {
    setJobStatus(event.target.value);
  };

  const handleSearch = () => {
    setPage(0);
    setPaginationModel({page: 0, pageSize});
    fetchYoutubeLinks(jobStatus, 0, pageSize);
  };

  const handlePaginationChange = model => {
    setPage(model.page);
    setPageSize(model.pageSize);
    setPaginationModel(model);
    fetchYoutubeLinks(jobStatus, model.page, model.pageSize);
     setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  // Updated pagination handlers
  const handlePageChange = (newPage) => {
    const updatedPaginationModel = { ...paginationModel, page: newPage };
    setPaginationModel(updatedPaginationModel);
    fetchData(newPage, paginationModel.pageSize, searchParams);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const handleGoToLastPage = async () => {
    if (totalPages > 0) {
      const lastPageIndex = totalPages - 1;
      const updatedPaginationModel = { ...paginationModel, page: lastPageIndex };
      setPaginationModel(updatedPaginationModel);
      await fetchData(lastPageIndex, paginationModel.pageSize, searchParams);
      setTimeout(() => window.scrollTo(0, 0), 0);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <Box sx={{p: 2, display: "flex", gap: 2, alignItems: "center", ml: 0}}>
          <FormControl variant="outlined" sx={{m: 0, width: "20ch"}}>
            <InputLabel id="status-label">Select Job Status</InputLabel>
            <Select
              labelId="status-label"
              id="status"
              name="status"
              value={jobStatus}
              onChange={handleJobStatusChange}
              onKeyDown={event => {
                if (event.key === "Enter") handleSearch();
              }}
              label="Status"
              sx={{minWidth: 150, p: 1.5}}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              <MenuItem value="1">Active</MenuItem>
              <MenuItem value="0">Inactive</MenuItem>
            </Select>
          </FormControl>

          <MDButton
            sx={{gridColumn: "span 2", background: properties.colors.bgcolorbluethem}}
            variant="gradient"
            color="info"
            type="button"
            onClick={handleSearch}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : "Search"}
          </MDButton>
        </Box>

        {error && (
          <Alert severity="error" sx={{mb: 2}}>
            {error}
          </Alert>
        )}

        <Box sx={{height: 500, width: "100%", ml: 0}}>
          <DataGrid
            rows={youtubeLinks}
            columns={columns}
            pageSizeOptions={[10, 20, 50]}
            paginationModel={paginationModel}
            // onPaginationModelChange={handlePaginationChange}
            paginationMode="server"
            rowCount={rowCount}
            loading={loading}
            getRowId={row => row.id}
            hideFooterPagination={true}
            sx={{
              fontFamily: properties.fontFamily,
              fontSize: "14px",
              height: "600px",
              "& .MuiDataGrid-scrollbar": {
                width: "0px !important",
              },
              "& .MuiDataGrid-row": {
                backgroundColor: "white !important"
              },
              "& .MuiDataGrid-cell": {
                backgroundColor: "white !important"
              },
              "& .MuiDataGrid-root": {
                height: "auto",
                maxHeight: "600px",
                backgroundColor: "white !important"
              },
              "& .MuiDataGrid-main": {
                backgroundColor: "white !important"
              }
            }}
          />
          <CustomPagination
                  page={paginationModel.page}
                  pageSize={paginationModel.pageSize}
                  totalRows={totalRows}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  onGoToLastPage={handleGoToLastPage}
                  loading={loading}
                />
        </Box>
      </MDBox>
    </DashboardLayout>
  );
}
